import { trackEvent } from '../utils/analytics';
import { config } from '../config/env';

interface WebSocketMessage {
  TYPE: number;
  PRICE: number;
  FLAGS: number;
  MARKET: string;
}

export class CryptoWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private subscribers: Set<(data: any) => void> = new Set();
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private lastMessageTime: number = Date.now();
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.connect();
    this.setupVisibilityHandler();
    this.setupHeartbeat();
  }

  private setupHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTime;
      if (timeSinceLastMessage > 30000) { // 30 seconds
        console.warn('No messages received for 30 seconds, reconnecting...');
        this.reconnect();
      }
    }, 10000); // Check every 10 seconds
  }

  private setupVisibilityHandler() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.cleanup();
      } else {
        this.connect();
      }
    });
  }

  private connect() {
    if (this.ws?.readyState === WebSocket.OPEN) return;

    try {
      this.cleanup();
      this.ws = new WebSocket('wss://streamer.cryptocompare.com/v2');
      
      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.setupPing();
        this.subscribe();
        trackEvent('WebSocket', 'Connected', 'Success');
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.lastMessageTime = Date.now();
          if (message.TYPE === 0 && message.PRICE) {
            this.notifySubscribers({ price: message.PRICE });
          }
        } catch (error) {
          console.error('WebSocket message parsing error:', error);
        }
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        this.handleDisconnect();
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        trackEvent('WebSocket', 'Error', 'Connection');
        this.handleDisconnect();
      };
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.handleDisconnect();
    }
  }

  private handleDisconnect() {
    this.cleanup();
    
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      this.reconnectTimeout = setTimeout(() => this.connect(), delay);
    } else {
      trackEvent('WebSocket', 'MaxReconnectAttempts', 'Error');
    }
  }

  private setupPing() {
    this.pingInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ action: 'ping' }));
      }
    }, 30000);
  }

  private subscribe() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const subRequest = {
        action: "SubAdd",
        subs: ["0~Coinbase~BTC~USD"],
        api_key: config.cryptoCompareApiKey
      };
      this.ws.send(JSON.stringify(subRequest));
    }
  }

  private reconnect() {
    this.cleanup();
    this.connect();
  }

  private cleanup() {
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private notifySubscribers(data: any) {
    this.subscribers.forEach(callback => callback(data));
  }

  public addSubscriber(callback: (data: any) => void) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  public disconnect() {
    this.cleanup();
    this.subscribers.clear();
  }
}